/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview Video Tutorial logic module.
 * Handles CRUD operations for video tutorials including fetching,
 * creating, updating, deleting tutorials and presigned URL management.
 * @module logic/videoTutorial
 */

import { refreshToken, signOut } from "../../utils/common.js";

const UNKNOWN_ERROR = "Unknown error";

/**
 * Fetches all video tutorials from the API
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Array>} Array of all video tutorials
 */
export const getVideoTutorials = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorial`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getVideoTutorials(navigate);
    }

    if (!response.ok) {
      logger.error("Error fetching video tutorials:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return [];
    }

    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    logger.error("Error fetching video tutorials:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches a video tutorial by ID from the API
 * @param {string} tutorialId - The ID of the video tutorial
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Video tutorial object or null if not found
 */
export const getVideoTutorialById = async (tutorialId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorial/${tutorialId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getVideoTutorialById(tutorialId, navigate);
    }

    if (!response.ok) {
      logger.error("Error fetching video tutorial:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return null;
    }

    return data;
  } catch (error) {
    logger.error("Error fetching video tutorial:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Creates a new video tutorial and generates a presigned URL for uploading the video file
 * @param {Object} tutorialData - Tutorial data with name, videoTutorialFileName, sectionId, and optional fields
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Object with tutorial and presignedUrl, or null on error
 */
export const createVideoTutorial = async (tutorialData, navigate) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(tutorialData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorial`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createVideoTutorial(tutorialData, navigate);
    }

    if (!response.ok) {
      logger.error("Error creating video tutorial:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      throw new Error(
        apiMessageOrGeneric(data?.message, "FAILED_TO_CREATE_VIDEO_TUTORIAL"),
      );
    }

    return {
      tutorial: data.tutorial || data,
      presignedUrl: data.presignedUrl || null,
    };
  } catch (error) {
    logger.error("Error creating video tutorial:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};

/**
 * Updates an existing video tutorial. If a new video file is provided, generates a new presigned URL
 * @param {string} tutorialId - The ID of the video tutorial to update
 * @param {Object} tutorialData - Updated tutorial data
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Object with tutorial and presignedUrl (if new file), or null on error
 */
export const updateVideoTutorial = async (
  tutorialId,
  tutorialData,
  navigate,
) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(tutorialData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorial/${tutorialId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await updateVideoTutorial(tutorialId, tutorialData, navigate);
    }

    if (!response.ok) {
      logger.error("Error updating video tutorial:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      throw new Error(
        apiMessageOrGeneric(data?.message, "FAILED_TO_UPDATE_VIDEO_TUTORIAL"),
      );
    }

    return {
      tutorial: data.tutorial || data,
      presignedUrl: data.presignedUrl || null,
    };
  } catch (error) {
    logger.error("Error updating video tutorial:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};

/**
 * Deletes a video tutorial and its associated video file from S3
 * @param {string} tutorialId - The ID of the video tutorial to delete
 * @param {Function} navigate - Navigation function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteVideoTutorial = async (tutorialId, navigate) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorial/${tutorialId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await deleteVideoTutorial(tutorialId, navigate);
    }

    if (!response.ok) {
      logger.error("Error deleting video tutorial:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.error("Error deleting video tutorial:", {
      err: sanitizeError(error),
    });
    return false;
  }
};
